import type { StatusClass, StatusClassCounts } from '@/types/stats';

/**
 * HTTP status semantics shared by the statistics views: status classes, their
 * color tokens and descriptions of common codes.
 */

export interface StatusClassConfig {
  /** Legend label, e.g. "5xx Server error" */
  label: string;
  /**
   * Class that paints a mark in the class color (sets `fill` for SVG and
   * `background-color` for HTML). Colors are the `--http-status-*` tokens in
   * index.css, validated for color-vision deficiencies in both themes.
   */
  className: string;
}

/**
 * Status classes in stacking order: server errors sit on the baseline (left
 * edge of a horizontal bar), so error shares line up and compare across rows
 * and time buckets.
 */
export const STATUS_CLASSES: readonly StatusClass[] = ['5xx', '4xx', '3xx', '2xx', '1xx'];

export const STATUS_CLASS_CONFIG: Readonly<Record<StatusClass, StatusClassConfig>> = {
  '1xx': { label: '1xx Informational', className: 'http-class-1xx' },
  '2xx': { label: '2xx Success', className: 'http-class-2xx' },
  '3xx': { label: '3xx Redirect', className: 'http-class-3xx' },
  '4xx': { label: '4xx Client error', className: 'http-class-4xx' },
  '5xx': { label: '5xx Server error', className: 'http-class-5xx' },
};

/**
 * Classes that are always listed; 1xx only when it has requests (nginx
 * rarely logs any)
 */
export function visibleStatusClasses(counts?: StatusClassCounts): StatusClass[] {
  return STATUS_CLASSES.filter((cls) => cls !== '1xx' || (counts?.[cls] ?? 0) > 0);
}

/**
 * Class of a status code; codes outside 100-599 count as server errors
 */
export function getStatusClass(status: number): StatusClass {
  if (status >= 100 && status < 200) return '1xx';
  if (status >= 200 && status < 300) return '2xx';
  if (status >= 300 && status < 400) return '3xx';
  if (status >= 400 && status < 500) return '4xx';
  return '5xx';
}

/**
 * Short descriptions of the codes operators meet most often
 */
const STATUS_DESCRIPTIONS: Readonly<Record<number, string>> = {
  200: 'OK',
  301: 'Moved permanently',
  302: 'Found (temporary redirect)',
  304: 'Not modified (served from client cache)',
  400: 'Bad request',
  401: 'Unauthorized (authentication required)',
  403: 'Forbidden',
  404: 'Not found',
  429: 'Too many requests (rate limited)',
  499: 'Client closed the connection before the response',
  500: 'Internal server error',
  502: 'Bad gateway (origin unreachable or invalid response)',
  503: 'Service unavailable',
  504: 'Gateway timeout (origin did not respond in time)',
};

const CLASS_DESCRIPTIONS: Readonly<Record<StatusClass, string>> = {
  '1xx': 'Informational response',
  '2xx': 'Successful response',
  '3xx': 'Redirect',
  '4xx': 'Client error',
  '5xx': 'Server error',
};

/**
 * Description of a status code, falling back to its class
 */
export function describeHttpStatus(status: number): string {
  return STATUS_DESCRIPTIONS[status] ?? CLASS_DESCRIPTIONS[getStatusClass(status)];
}
